import { useState } from 'react';
import { Select } from '../../design-system/components/Select';
import { Spinner } from '../../design-system/components/Spinner';
import { EmptyState } from '../../shared/components/EmptyState';
import { ErrorState } from '../../shared/components/ErrorState';
import { StatCard } from '../../shared/components/StatCard';
import type { DashboardPeriod } from '../../shared/data/queryKeys';
import { useAuthStore } from '../auth/store';
import { useDashboardStats } from './hooks';

const PERIOD_OPTIONS = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
] as const;

/** Faixa de coaching para talk ratio do closer (fração 0–1). */
const TALK_RATIO_BAND = { start: 0.4, end: 0.6 } as const;

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${Math.round(value * 100)}%`;
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(value);
}

export function HomeScreen() {
  const user = useAuthStore((s) => s.user);
  const [period, setPeriod] = useState<DashboardPeriod>('7d');
  const query = useDashboardStats(period);

  if (query.isPending) {
    return (
      <div className="center full-height">
        <Spinner label="Carregando métricas" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => query.refetch()}
        title="Não foi possível carregar suas métricas"
      />
    );
  }

  const stats = query.data;
  const insightsTotal = Object.values(stats.insights_by_category ?? {}).reduce<number>(
    (acc, value) => acc + (typeof value === 'number' ? value : 0),
    0,
  );

  return (
    <div className="dash-section">
      <div className="dash-section__header">
        <div>
          <h1 className="dash-section__title">Olá, {user?.email ?? 'closer'}</h1>
          <p className="muted">Resumo do período selecionado.</p>
        </div>
        <Select
          label="Período"
          options={PERIOD_OPTIONS}
          value={period}
          onChange={(e) => setPeriod(e.target.value as DashboardPeriod)}
        />
      </div>

      {stats.meetings_total === 0 ? (
        <EmptyState
          title="Sem reuniões no período"
          description="Assim que você concluir uma reunião, as métricas aparecerão aqui."
        />
      ) : (
        <>
          <div className="dash-grid">
            <StatCard
              label="Reuniões"
              value={formatNumber(stats.meetings_total)}
              helper={`${stats.meetings_completed} concluídas`}
            />
            <StatCard label="Com follow-up" value={formatNumber(stats.meetings_with_followup)} />
            <StatCard
              label="Talk ratio médio (closer)"
              value={formatPercent(stats.avg_talk_ratio_closer)}
              helper="Faixa recomendada: 40–60%"
              {...(stats.avg_talk_ratio_closer !== null && stats.avg_talk_ratio_closer !== undefined
                ? {
                    gauge: {
                      value: stats.avg_talk_ratio_closer,
                      bandStart: TALK_RATIO_BAND.start,
                      bandEnd: TALK_RATIO_BAND.end,
                      label: `${formatPercent(stats.avg_talk_ratio_closer)} — faixa recomendada: 40–60%`,
                    },
                  }
                : {})}
            />
            <StatCard
              label="Sentimento médio"
              value={formatNumber(stats.avg_sentiment)}
              helper="-1 negativo · +1 positivo"
              {...(stats.avg_sentiment !== null && stats.avg_sentiment !== undefined
                ? {
                    gauge: {
                      // Escala -1..+1 normalizada para 0..1; saudável = acima de 0.
                      value: (stats.avg_sentiment + 1) / 2,
                      bandStart: 0.5,
                      bandEnd: 1,
                      label: `${stats.avg_sentiment.toFixed(2)} — meta: sentimento acima de 0`,
                    },
                  }
                : {})}
            />
            <StatCard label="Insights" value={formatNumber(insightsTotal)} />
          </div>
        </>
      )}
    </div>
  );
}
